import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";

export default function LoginModal({ isOpen, onClose }) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const { login, signup, googleLogin } = useAuth();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const [gisLoaded, setGisLoaded] = useState(false);

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      if (isLogin) {
        await login(email, password);
        setSuccess("Login successful! Redirecting...");
        setTimeout(() => {
          onClose();
          navigate("/");
        }, 1500);
      } else {
        // Signup requires phone number — redirect to the full login/signup page
        onClose();
        navigate("/login?mode=signup");
      }
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  // initialize Google Identity Services button
  useEffect(() => {
    if (!isOpen || !clientId) return;
    const id = 'gsi-script-modal';
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = id;
    document.body.appendChild(script);

    script.onload = () => {
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            try {
              const idToken = response?.credential;
              if (!idToken) throw new Error('Missing credential from Google');
              await googleLogin(idToken);
              setSuccess('Google Sign-In successful! Redirecting...');
              setTimeout(() => {
                onClose();
                navigate('/');
              }, 1500);
            } catch (err) {
              setError(err?.message || String(err));
            }
          }
        });

        const container = document.getElementById('g_id_signin_modal');
        if (container) {
          window.google.accounts.id.renderButton(container, { theme: 'outline', size: 'large' });
        }
        setGisLoaded(true);
      } catch (err) {
        // Google init error
      }
    };

    script.onerror = () => {
      setError('Failed to load Google Identity Services script.');
      setGisLoaded(false);
    };

    return () => {
      try { document.getElementById(id) && document.getElementById(id).remove(); } catch(e){}
      setGisLoaded(false);
    };
  }, [isOpen, clientId, googleLogin, navigate, onClose]);

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] px-4 py-6">
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative border-t-4 border-[#4299f5] max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          aria-label="Close modal"
        >
          ×
        </button>

        <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-2 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
          {isLogin ? "Welcome Back" : "Create Account"}
        </h2>
        <p className="text-center text-gray-600 mb-4 text-sm">
          {isLogin ? "Login to continue" : "Sign up to get started"}
        </p>

        {success && (
          <p className="text-green-600 text-center font-medium mb-3 bg-green-50 py-2 rounded-lg border border-green-200 text-sm">
            {success}
          </p>
        )}

        {error && (
          <div className="text-center">
            <p className="text-red-600 text-center font-medium mb-2 bg-red-50 py-2 rounded-lg border border-red-200 text-sm">{error}</p>
            {error.toLowerCase().includes("email not verified") && (
              <p className="text-xs text-[#4299f5] mt-2">
                Didn't receive verification? <a href="/resend-verification" className="font-semibold underline">Resend email</a>
              </p>
            )}
          </div>
        )}

        <form className="space-y-3 sm:space-y-4" onSubmit={handleSubmit}>
          {!isLogin && (
            <div>
              <label className="block mb-1 text-gray-900 font-semibold text-sm">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4299f5] focus:outline-none bg-gray-50 text-gray-900 text-sm"
              />
            </div>
          )}
          <div>
            <label className="block mb-1 text-gray-900 font-semibold text-sm">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4299f5] focus:outline-none bg-gray-50 text-gray-900 text-sm"
            />
          </div>
          <div>
            <label className="block mb-1 text-gray-900 font-semibold text-sm">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4299f5] focus:outline-none bg-gray-50 text-gray-900 text-sm"
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 rounded-lg font-semibold text-white bg-[#4299f5] hover:bg-[#3a8fe5] transition-all shadow-lg text-sm sm:text-base"
            style={{fontFamily: 'Montserrat, sans-serif'}}
          >
            {isLogin ? "Login" : "Sign Up"}
          </button>
        </form>

        {isLogin && (
          <div className="mt-2 text-xs text-right">
            <a href="/forgot-password" className="text-[#4299f5] hover:underline font-medium">Forgot password?</a>
          </div>
        )}

        <div className="flex items-center my-3">
          <hr className="flex-grow border-gray-300" />
          <span className="mx-2 text-gray-500 text-xs">OR</span>
          <hr className="flex-grow border-gray-300" />
        </div>

        <div id="g_id_signin_modal" className="flex justify-center mt-0" />

        <p className="text-center mt-3 text-gray-600 text-xs sm:text-sm">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={toggleForm}
            className="text-[#4299f5] hover:text-[#3a8fe5] font-semibold hover:underline"
          >
            {isLogin ? "Sign Up" : "Login"}
          </button>
        </p>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}